import React, { useState, useEffect, useCallback } from 'react';
import styles from './MagicLinkLanding.module.css';
import * as api from '../../../api/Passwordless';
import AgentsPrompt from '../AgentsPrompt/AgentsPrompt';
import ClosedAccountModal from '../ClosedAccountModal/ClosedAccountModal';

// Landing page the magic link points at. It runs in the SAME browser that
// requested the link (that's the device binding), reads the token from the URL,
// and POSTs it to verify — a GET alone never consumes the token. On success we
// redirect into the tenant app; if the email maps to several accounts we show a
// picker.
export default function MagicLinkLanding() {
  const token = new URLSearchParams(window.location.search).get('token');
  const [status, setStatus] = useState('verifying'); // verifying | error
  const [accounts, setAccounts] = useState(null);
  // MT-1765: account closed for a Terms of Service violation.
  const [closedAccount, setClosedAccount] = useState(null);
  const [suspendedMessage, setSuspendedMessage] = useState('');

  const verify = useCallback(
    async (agentLoginId) => {
      setStatus('verifying');
      try {
        const response = await api.emailVerify(token, agentLoginId);
        if (response.data.requires_account_selection) {
          setAccounts(
            response.data.accounts.map((account) => ({
              id: account.agent_login_id,
              company: account.company || '-',
              name: account.name,
              pin: account.pin,
              status_id: 1,
              status: 'Active',
              is_should_sign_up: false,
            }))
          );
          return;
        }
        if (response.data.redirect_url) {
          window.location.href = response.data.redirect_url;
          return;
        }
        setStatus('error');
      } catch (e) {
        const data = (e.response && e.response.data) || {};
        if (data.closed_account) {
          setClosedAccount(data.closed_account);
        } else if (data.account_suspended) {
          setSuspendedMessage(data.errors);
        }
        setStatus('error');
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    verify();
  }, [token, verify]);

  return (
    <div className={styles.MagicLinkLanding}>
      {accounts && accounts.length > 1 && (
        <AgentsPrompt
          isOpen
          agents={accounts}
          optionBtnLabel="Login"
          onClose={() => setAccounts(null)}
          onLoginOptionClick={(agentLoginId) => {
            setAccounts(null);
            verify(agentLoginId);
          }}
          onSignUpOptionClick={() => {}}
        />
      )}

      {status === 'verifying' && !accounts && (
        <div className={styles.Card}>
          <div className={styles.Spinner} />
          <div className={styles.Title}>Signing you in…</div>
        </div>
      )}

      {closedAccount && (
        <ClosedAccountModal
          content={closedAccount}
          onClose={() => {
            window.location.href = '/login/';
          }}
        />
      )}

      {status === 'error' && suspendedMessage && (
        <div className={styles.Card}>
          <div className={styles.Text}>{suspendedMessage}</div>
          <a className={styles.Button} href="/login/">
            Back to sign in
          </a>
        </div>
      )}

      {status === 'error' && !closedAccount && !suspendedMessage && (
        <div className={styles.Card}>
          <div className={styles.Title}>This link didn’t work</div>
          <div className={styles.Text}>
            It may have expired, already been used, or been opened on a
            different device than the one that requested it. Sign-in links work
            only in the browser they were requested from.
          </div>
          <a className={styles.Button} href="/login/">
            Back to sign in
          </a>
        </div>
      )}
    </div>
  );
}
